import React, { useCallback, useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import styled from 'styled-components';

import { Button } from 'src/app/components/lib/button';
import { ControlBar, ControlSearchBox } from 'src/app/components/lib/control-bar';
import { ControlGroup, ControlSpacer } from 'src/app/components/lib/control-bar/control-bar.styles';
import { RefreshButton } from 'src/app/components/lib/control-bar/refresh';
import { DateRangePicker } from 'src/app/components/lib/date-range-picker';
import { PageLoader } from 'src/app/components/lib/loader';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Select } from 'src/app/components/lib/select';
import { FixedHeightDataGrid } from 'src/app/components/lib/table/fixed-height-data-grid';
import ActionsMenu from 'src/app/layout/main/actions/actions-menu';
import { handleReportDownloadV2 } from 'src/app/pages/reports-v2/utils';
import { useGetMarketingCampaignQuery } from 'src/app/queries/marketing/get-campaign';
import { Modal, useModal } from 'src/app/state/modals';
import { useNotificationActions } from 'src/app/state/notifications';

import {
  useCampaignEmailRecipientsQuery,
  type EmailRecipient,
} from '../campaign-content/use-campaign-email-recipients';
import { useCampaignPushRecipientsQuery } from '../campaign-content/use-campaign-push-recipients';
import { useCampaignSmsRecipientsQuery } from '../campaign-content/use-campaign-sms-recipients';

import { useEmailColumns } from './use-email-columns';
import { useExportAudienceV2 } from './use-export-audience';
import { usePushColumns } from './use-push-columns';
import { useSmsColumns } from './use-sms-columns';

import type { PushRecipient } from '../campaign-content/use-campaign-push-recipients';
import type { SmsRecipient } from '../campaign-content/use-campaign-sms-recipients';
import type { GridRowModel, GridSortModel } from '@mui/x-data-grid-pro';
import type { Action } from 'src/app/layout/main/actions/actions-menu';

type RecipientListProps = {
  marketingCampaignId: string;
};

type CampaignChannel = 'email' | 'push' | 'sms';

export function RecipientListV2({ marketingCampaignId }: RecipientListProps) {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [disableDownload, setDisableDownload] = useState(true);
  const [channel, setChannel] = useState<CampaignChannel>('email');

  const defaultDateRange: [Date, Date] = useMemo(
    () => [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()],
    []
  );

  const [dateRange, setDateRangeState] = useState<[Date, Date]>(defaultDateRange);

  const setDateRange = useCallback(
    (dateRange: [Date | null, Date | null]) => {
      setDateRangeState([dateRange[0] ?? defaultDateRange[0], dateRange[1] ?? defaultDateRange[1]]);
    },
    [defaultDateRange]
  );

  const exportTableModal = useModal(Modal.exportTable);
  const printTableModal = useModal(Modal.printTable);
  const confirmModal = useModal(Modal.confirm);
  const notifications = useNotificationActions();

  const { data: campaignData, isLoading: isCampaignLoading } = useGetMarketingCampaignQuery({
    marketingCampaignId: Number(marketingCampaignId),
  });

  const isAutomatedCampaign = !!campaignData?.EventTypeId;

  const { mutateAsync: getReportFile } = useExportAudienceV2();

  const handleReportDownload = useCallback(
    async (reportName: string) => {
      try {
        await handleReportDownloadV2({
          reportName,
          getReportFile: () =>
            getReportFile({
              marketingCampaignId: Number(marketingCampaignId),
              startDate: isAutomatedCampaign ? dateRange[0].toISOString() : undefined,
              endDate: isAutomatedCampaign ? dateRange[1].toISOString() : undefined,
            }),
          excelFormat: true,
          enableDownload: setDisableDownload,
        });
        notifications.success({ message: 'Recipients exported successfully' });
      } catch (error) {
        notifications.error({ message: 'Failed to export recipients' });
      }
    },
    [getReportFile, marketingCampaignId, isAutomatedCampaign, dateRange]
  );

  const debouncedGetReportFile = _.debounce(handleReportDownload, 1000, { leading: true, trailing: false });

  const getCampaignRecipientsParams = useMemo(
    () => ({
      marketingCampaignId,
      pageSize,
      currentPage: page,
      searchTerm,
      sortAscending: sortModel[0]?.sort === 'asc',
      sortColumn: sortModel[0]?.field,
      startDate: isAutomatedCampaign ? dateRange[0].toISOString() : undefined,
      endDate: isAutomatedCampaign ? dateRange[1].toISOString() : undefined,
    }),
    [marketingCampaignId, page, pageSize, sortModel, searchTerm, dateRange, isAutomatedCampaign]
  );

  useEffect(() => {
    if (!campaignData) {
      return;
    }

    if (campaignData?.EmailConfig?.IsActive) {
      setChannel('email');
    } else if (campaignData?.SMSConfig?.IsActive) {
      setChannel('sms');
    } else {
      setChannel('push');
    }
  }, [campaignData]);

  // when the channel changes, reset the page, sort model, and search term
  useEffect(() => {
    setPage(0);
    setSortModel([{ field: 'name', sort: 'asc' }]);
  }, [channel]);

  const { data: campaignEmailRecipientsData, isLoading: isCampaignEmailRecipientsLoading } =
    useCampaignEmailRecipientsQuery(getCampaignRecipientsParams, {
      enabled: !isCampaignLoading && channel === 'email',
    });

  const { data: campaignPushRecipientsData, isLoading: isCampaignPushRecipientsLoading } =
    useCampaignPushRecipientsQuery(getCampaignRecipientsParams, {
      enabled: !isCampaignLoading && channel === 'push',
    });

  const { data: campaignSmsRecipientsData, isLoading: isCampaignSmsRecipientsLoading } = useCampaignSmsRecipientsQuery(
    getCampaignRecipientsParams,
    {
      enabled: !isCampaignLoading && channel === 'sms',
    }
  );

  const pushIsPending = isCampaignPushRecipientsLoading && channel === 'push';
  const smsIsPending = isCampaignSmsRecipientsLoading && channel === 'sms';
  const emailIsPending = isCampaignEmailRecipientsLoading && channel === 'email';

  const emailColumns = useEmailColumns();
  const smsColumns = useSmsColumns();
  const pushColumns = usePushColumns();

  const onRefresh = async ({
    currentPage = 0,
    newPageSize = pageSize,
    newSortModel = sortModel,
  }: {
    currentPage?: number;
    newPageSize?: number;
    newSortModel?: GridSortModel;
  } = {}) => {
    setPage(currentPage);
    setPageSize(newPageSize);
    setSortModel(newSortModel);
    setSearchTerm(''); // Reset search term on refresh
  };

  const handlePaginationChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
    setPage(0);
  };

  const handleAudienceExport = () => {
    confirmModal.openModal({
      automationId: 'export-recipients-confirm-modal',
      confirmButtonAutomationId: 'export-recipients-confirm-modal-confirm-button',
      title: 'Export recipients',
      loading: disableDownload,
      helpText: `Are you sure you want to export all recipients? Only 200k recipients per channel will be exported.`,
      onConfirm: async () => {
        await debouncedGetReportFile(`Campaign recipients ${new Date().toLocaleDateString()}`);
      },
    });
  };

  const listActions: Action[] = [
    {
      label: 'Print',
      visible: true,
      onClick: () => {
        printTableModal.openModal();
      },
    },
    {
      label: 'Export',
      visible: true,
      onClick: () => {
        exportTableModal.openModal({
          title: 'Campaign Recipients',
        });
      },
    },
  ];

  const debouncedOnSearchChange = _.debounce((value: string) => {
    setSearchTerm(value);
    setPage(0);
  }, 500);

  const searchPlaceholder = useMemo(() => {
    if (channel === 'email') {
      return 'Search by name or email...';
    }
    if (channel === 'sms') {
      return 'Search by name or phone number...';
    }

    return 'Search by name...';
  }, [channel]);

  const channelDropdownOptions = useMemo(() => {
    const result: { id: string; label: string; value: CampaignChannel }[] = [];
    if (campaignData?.EmailConfig?.IsActive) {
      result.push({ id: 'email', label: 'Email', value: 'email' });
    }
    if (campaignData?.SMSConfig?.IsActive) {
      result.push({ id: 'sms', label: 'SMS', value: 'sms' });
    }
    if (campaignData?.PushConfig?.IsActive) {
      result.push({ id: 'push', label: 'Push', value: 'push' });
    }
    return result;
  }, [campaignData]);

  const isLoading = emailIsPending || pushIsPending || smsIsPending || isCampaignLoading;

  return (
    <RecipientListContainer>
      <ControlBar>
        <ControlGroup grow>
          <ControlSearchBox
            automationId='search-input'
            initialValue={searchTerm}
            placeholder={searchPlaceholder}
            onChange={debouncedOnSearchChange}
          />
          {isAutomatedCampaign && (
            <DateRangePicker value={dateRange} onDateChange={(dateRange) => setDateRange(dateRange)} />
          )}
          <ChannelSelect
            automationId='recipients-channel-dropdown'
            id='recipients-channel-dropdown'
            value={channel}
            onChange={({ target }) => setChannel(target.value)}
          >
            {channelDropdownOptions.map((option: { id: string; label: string; value: CampaignChannel }) => (
              <MenuItem
                automationId={`channel-campaign-attribution-channel-dropdown-${option.id}`}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </ChannelSelect>
          <ControlSpacer />
          <ActionsMenu actions={listActions} />
          <Button automationId='export-recipients' label='Export recipients' onClick={handleAudienceExport} />
          <RefreshButton onClick={() => void onRefresh()} />
        </ControlGroup>
      </ControlBar>

      {isLoading && <PageLoader />}
      {!isLoading && channel === 'email' && (
        <FixedHeightDataGrid
          columns={emailColumns}
          getRowId={(row: GridRowModel) => (row as EmailRecipient).CustomerId}
          height='100%'
          hideFooter={false}
          name='campaign-email-list'
          page={page}
          pageSize={pageSize}
          pagination
          paginationMode='server'
          rowCount={campaignEmailRecipientsData?.TotalRows ?? 0}
          rows={campaignEmailRecipientsData?.Data ?? []}
          sortingMode='server'
          sortModel={sortModel}
          onPageChange={handlePaginationChange}
          onPageSizeChange={handlePageSizeChange}
          onSortModelChange={handleSortModelChange}
        />
      )}
      {!isLoading && channel === 'sms' && (
        <FixedHeightDataGrid
          columns={smsColumns}
          getRowId={(row: GridRowModel) => (row as SmsRecipient).CustomerId}
          height='100%'
          hideFooter={false}
          name='campaign-sms-list'
          page={page}
          pageSize={pageSize}
          pagination
          paginationMode='server'
          rowCount={campaignSmsRecipientsData?.TotalRows ?? 0}
          rows={campaignSmsRecipientsData?.Data ?? []}
          sortingMode='server'
          sortModel={sortModel}
          onPageChange={handlePaginationChange}
          onPageSizeChange={handlePageSizeChange}
          onSortModelChange={handleSortModelChange}
        />
      )}
      {!isLoading && channel === 'push' && (
        <FixedHeightDataGrid
          columns={pushColumns}
          getRowId={(row: GridRowModel) => (row as PushRecipient).CustomerId}
          height='100%'
          hideFooter={false}
          name='campaign-push-list'
          page={page}
          pageSize={pageSize}
          pagination
          paginationMode='server'
          rowCount={campaignPushRecipientsData?.TotalRows ?? 0}
          rows={campaignPushRecipientsData?.Data ?? []}
          sortingMode='server'
          sortModel={sortModel}
          onPageChange={handlePaginationChange}
          onPageSizeChange={handlePageSizeChange}
          onSortModelChange={handleSortModelChange}
        />
      )}
    </RecipientListContainer>
  );
}

const RecipientListContainer = styled.div`
  width: 100%;
  height: 600px;
  background-color: var(--color-brand-primary-white);
  border-radius: 8px;
  padding: var(--sizes-20);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
`;

const ChannelSelect = styled(Select)`
  width: 300px;
`;
